import React, { useState } from 'react';
import { authAPI } from '../services/api';

function Login({ onLoginSuccess }) {
  const [isLogin, setIsLogin]   = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', password_confirmation: '' });
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = isLogin
        ? await authAPI.login({ email: formData.email, password: formData.password })
        : await authAPI.register(formData);
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLoginSuccess(response.data.user);
    } catch (err) {
      setError(err.response?.data?.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background orbs */}
      <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-violet-600/10 blur-[120px] pointer-events-none animation-delay-2000" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-[120px] pointer-events-none" />
      <div className="absolute top-[40%] right-[20%] w-[300px] h-[300px] rounded-full bg-indigo-600/8 blur-[100px] pointer-events-none animation-delay-4000" />

      {/* Card */}
      <div className="relative z-10 w-full max-w-sm bg-zinc-900/80 backdrop-blur-xl border border-white/[0.07] rounded-2xl p-8 shadow-2xl shadow-black/50">

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-blue-600 shadow-lg shadow-violet-500/30 mb-4">
            <span className="text-white font-bold text-xl">C</span>
          </div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
            ContentVertex
          </h1>
          <p className="text-xs text-zinc-500 mt-1">Publish More. Rank Higher. Win Your Niche.</p>
        </div>

        {/* Tab switcher */}
        <div className="flex bg-zinc-800/60 rounded-xl p-1 mb-6">
          {['Login', 'Sign Up'].map((label, i) => (
            <button
              key={label}
              onClick={() => setIsLogin(i === 0)}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200
                ${(i === 0) === isLogin
                  ? 'bg-zinc-700 text-zinc-100 shadow'
                  : 'text-zinc-500 hover:text-zinc-300'
                }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm mb-4">
            <span className="text-base leading-none">⚠</span>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <Field label="Full Name" type="text" placeholder="Your name" value={formData.name} onChange={set('name')} />
          )}
          <Field label="Email" type="email" placeholder="you@example.com" value={formData.email} onChange={set('email')} />
          <Field label="Password" type="password" placeholder="••••••••" value={formData.password} onChange={set('password')} />
          {!isLogin && (
            <Field label="Confirm Password" type="password" placeholder="••••••••" value={formData.password_confirmation} onChange={set('password_confirmation')} />
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white font-semibold text-sm shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed mt-2"
          >
            {loading ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Processing…
              </>
            ) : (
              isLogin ? 'Continue to Dashboard →' : 'Start Creating for Free →'
            )}
          </button>
        </form>

        {/* Social proof */}
        <div className="flex items-center justify-center gap-2 mt-5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <p className="text-xs text-zinc-500">Join 1,200+ content creators publishing with AI</p>
        </div>

        {/* Footer link */}
        <p className="text-center text-xs text-zinc-600 mt-4">
          {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
          <button onClick={() => setIsLogin(!isLogin)} className="text-violet-400 hover:text-violet-300 transition-colors font-medium">
            {isLogin ? 'Sign Up' : 'Login'}
          </button>
        </p>
      </div>
    </div>
  );
}

// Small reusable field
const Field = ({ label, type, placeholder, value, onChange }) => (
  <div>
    <label className="block text-xs font-semibold text-zinc-400 mb-1.5">{label}</label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required
      className="w-full bg-zinc-800/60 border border-white/[0.07] rounded-xl px-4 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/30 transition-all"
    />
  </div>
);

export default Login;
